import { gql, useMutation } from '@apollo/client'
import { cartFragment } from '@emico-hooks/cart-fragment'
import { useCartId } from '@emico-hooks/cart-id'
import { useAuthorizationContext } from '@emico-hooks/login-token'
import * as React from 'react'

import { query as getCustomerVouchersQuery } from './customerVouchers.query'
import { appliedCouponsQuery } from '../../../../customer/srsVouchers.query'
import {
    RemoveCouponFromCartInput,
    RemoveCouponFromCartOutput,
} from '../../../../graphql/schema.generated'
import Button, { ButtonProps } from '../../../../input/Button'
import { M2ApiResponseError } from '../../../../utils/RestApi'
import withSentry from '../../../../utils/withSentry'

const removeVoucherFromCart = gql`
    mutation RemoveCouponFromCart($input: RemoveCouponFromCartInput!) {
        removeCouponFromCart(input: $input) {
            cart {
                ...CartFragment
            }
        }
    }
    ${cartFragment}
`

interface OwnProps {
    code: string
    hideLoader?: boolean
    className?: string
    onRemove?(): void
}

type Props = OwnProps & Omit<ButtonProps, 'category' | 'onClick' | 'name'>

const RemoveVoucherButton = ({
    code,
    onRemove,
    variant = 'primary',
    hideLoader = false,
    className,
    children,
    ...others
}: Props) => {
    const authorizationContext = useAuthorizationContext()
    const [isLoading, setLoading] = React.useState<boolean>(false)

    const cartId = useCartId()

    // eslint-disable-next-line no-restricted-syntax
    const [removeLoyaltyVoucher] = useMutation<
        RemoveCouponFromCartOutput,
        { input: RemoveCouponFromCartInput }
    >(removeVoucherFromCart, {
        refetchQueries: () => [
            {
                query: appliedCouponsQuery,
                variables: { cartId },
                context: authorizationContext,
            },
            {
                query: getCustomerVouchersQuery,
                context: authorizationContext,
            },
        ],
        context: authorizationContext,
        awaitRefetchQueries: true,
    })

    const handleClick = async () => {
        setLoading(true)
        try {
            await removeLoyaltyVoucher({
                variables: {
                    input: {
                        coupon_code: code,
                        cart_id: cartId ?? '',
                    },
                },
            })

            if (onRemove) {
                onRemove()
            }

            // Refresh cart to update totals
            //await dispatch(getCartDetails({ forceRefresh: true }))
            setLoading(false)
        } catch (err) {
            setLoading(false)

            if (!(err instanceof Error)) {
                return
            }
            let errorMessage = err.message

            if (err instanceof M2ApiResponseError) {
                errorMessage = err.userMessage
            }

            console.error(errorMessage)

            withSentry((Sentry) => {
                Sentry.captureException(err)
            })

            return
        }
    }

    return (
        <>
            <Button
                name="Remove voucher"
                category="user.rewards"
                variant={variant}
                onClick={handleClick}
                loading={!hideLoader && isLoading}
                disabled={isLoading}
                {...others}
                className={className}
            >
                {children && typeof children === 'function'
                    ? children(isLoading)
                    : children}
            </Button>
        </>
    )
}

export default RemoveVoucherButton
